
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect } from 'react';
import { SCENARIOS, TUTORIAL_CAMPAIGN } from '../constants';
import { INDUSTRY_CATEGORIES } from '../constants/industryCategories';
import { playUiSfx } from '../utils/uiAudio';
import { getCareerSummary } from '../utils/playerAnalytics';
import PlayerStatsPanel from './ui/PlayerStatsPanel';

interface StartScreenProps {
  onStart: (aiEnabled: boolean, scenarioId?: string, industryCategory?: string) => void;
  onResume: () => void;
  hasSave: boolean;
}

const StartScreen: React.FC<StartScreenProps> = ({ onStart, onResume, hasSave }) => {
  const [aiEnabled, setAiEnabled] = useState(true);
  const [tab, setTab] = useState<'sandbox' | 'missions' | 'training' | 'faq'>('training');
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [careerSummary, setCareerSummary] = useState(() => getCareerSummary(TUTORIAL_CAMPAIGN.length));

  useEffect(() => {
      console.log('✅ INDUSTRY_CATEGORIES loaded:', INDUSTRY_CATEGORIES);
      const savedProgress = localStorage.getItem('compliance_command_training_progress');
      if (savedProgress) {
          setTrainingProgress(parseInt(savedProgress));
      } else {
          setTrainingProgress(0);
      }
      setCareerSummary(getCareerSummary(TUTORIAL_CAMPAIGN.length));
  }, []);

  const handleTabChange = (t: 'sandbox' | 'missions' | 'training' | 'faq') => {
      playUiSfx('click');
      setTab(t);
  };

  return (
    <div data-testid="start-screen" className="absolute inset-0 z-50 bg-[#050505] text-white font-sans overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y">
      {/* Abstract Background */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(16,185,129,0.1),transparent_70%)]"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-indigo-600/20 blur-[100px]"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:40px_40px]"></div>
      </div>

      <div className="max-w-5xl w-full mx-auto grid grid-cols-12 gap-6 md:gap-8 relative z-10 p-4 sm:p-6 py-6 md:py-10 min-h-min">
        
        {/* Left Column: Menu */}
        <div className="col-span-12 md:col-span-4 flex flex-col justify-center">
            <h1 className="text-6xl font-black mb-2 tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
                ZERO<br/>TRUST
            </h1>
            <p className="text-emerald-500 font-mono text-xs uppercase tracking-[0.2em] mb-8">
                Compliance Command v2.1
            </p>

            <div className="space-y-2">
                <button 
                    onClick={() => handleTabChange('training')}
                    className={`w-full text-left p-6 rounded-2xl border transition-all duration-300 group relative overflow-hidden ${tab === 'training' ? 'bg-cyan-600 text-white border-cyan-500 shadow-[0_0_30px_rgba(6,182,212,0.3)]' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-white'}`}
                >
                    <div className="relative z-10">
                        <div className="font-bold text-xl mb-1">Training Academy</div>
                        <div className={`text-xs ${tab === 'training' ? 'text-cyan-200' : 'text-slate-500'}`}>Learn the systems. Progressive modules.</div>
                    </div>
                </button>

                <button 
                    onClick={() => handleTabChange('sandbox')}
                    data-testid="sandbox-tab"
                    className={`w-full text-left p-6 rounded-2xl border transition-all duration-300 group relative overflow-hidden ${tab === 'sandbox' ? 'bg-white text-black border-white' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-white'}`}
                >
                    <div className="relative z-10">
                        <div className="font-bold text-xl mb-1">Sandbox Mode</div>
                        <div className={`text-xs ${tab === 'sandbox' ? 'text-slate-600' : 'text-slate-500'}`}>Unrestricted architectural simulation.</div>
                    </div>
                </button>

                <button 
                    onClick={() => handleTabChange('missions')}
                    className={`w-full text-left p-6 rounded-2xl border transition-all duration-300 group relative overflow-hidden ${tab === 'missions' ? 'bg-indigo-600 text-white border-indigo-500 shadow-[0_0_30px_rgba(79,70,229,0.3)]' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-white'}`}
                >
                    <div className="relative z-10">
                        <div className="font-bold text-xl mb-1">Scenario Ops</div>
                        <div className={`text-xs ${tab === 'missions' ? 'text-indigo-200' : 'text-slate-500'}`}>Crisis management & historical audits.</div>
                    </div>
                </button>

                <button 
                    onClick={() => handleTabChange('faq')}
                    className={`w-full text-left p-6 rounded-2xl border transition-all duration-300 group relative overflow-hidden ${tab === 'faq' ? 'bg-emerald-900/30 text-emerald-400 border-emerald-500/50' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-white'}`}
                >
                    <div className="relative z-10">
                        <div className="font-bold text-xl mb-1">System Protocols</div>
                        <div className={`text-xs ${tab === 'faq' ? 'text-emerald-300' : 'text-slate-500'}`}>Identity, Privacy, and Directives.</div>
                    </div>
                </button>
            </div>
            
            {hasSave && (
                <button
                    type="button"
                    onClick={() => { playUiSfx('click'); onResume(); }}
                    data-testid="resume-game"
                    className="w-full mt-6 px-6 py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-500 transition-colors uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(79,70,229,0.3)]"
                >
                    📂 Resume Saved Game
                </button>
            )}

            <div className="mt-8 pt-8 border-t border-white/10">
                 <label className="flex items-center gap-3 cursor-pointer group">
                     <div className={`w-10 h-6 rounded-full p-1 transition-colors ${aiEnabled ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                         <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${aiEnabled ? 'translate-x-4' : ''}`}></div>
                     </div>
                     <input type="checkbox" className="hidden" checked={aiEnabled} onChange={e => setAiEnabled(e.target.checked)} />
                     <div className="text-sm font-bold text-slate-300 group-hover:text-white transition-colors">Risk Officer AI</div>
                 </label>
            </div>
        </div>

        {/* Right Column: Details */}
        <div className="col-span-12 md:col-span-8 bg-black/40 backdrop-blur-xl rounded-3xl border border-white/10 p-1 flex flex-col">
            <div className="bg-[#0a0a0a] rounded-[20px] p-6 sm:p-8 border border-white/5">
                
                {tab === 'training' && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-cyan-400 uppercase tracking-widest">Academy Modules</h2>
                            <span className="text-xs font-mono text-slate-500">{Math.min(trainingProgress, TUTORIAL_CAMPAIGN.length)} / {TUTORIAL_CAMPAIGN.length} COMPLETED</span>
                        </div>

                        {careerSummary.totalRuns > 0 && (
                            <div
                                className="mb-6 p-4 rounded-xl border border-indigo-900/50 bg-indigo-950/20"
                                data-testid="start-screen-career-summary"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-indigo-300">Professional Record</h3>
                                    <span className="text-[10px] font-mono text-slate-500">
                                        {careerSummary.totalRuns} runs · {careerSummary.winRate}% win rate
                                    </span>
                                </div>
                                <PlayerStatsPanel compact />
                            </div>
                        )}
                        
                        <div className="grid gap-4">
                            {TUTORIAL_CAMPAIGN.map((s, idx) => {
                                const isLocked = idx > trainingProgress;
                                const isCompleted = idx < trainingProgress;
                                
                                return (
                                    <div key={s.id} className={`p-5 rounded-xl border transition-all ${isLocked ? 'bg-black/40 border-white/5 opacity-50 cursor-not-allowed' : 'bg-cyan-950/20 border-cyan-900/50 hover:bg-cyan-900/30 hover:border-cyan-500/50 cursor-default group'}`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isLocked ? 'bg-slate-800 text-slate-500' : isCompleted ? 'bg-emerald-500 text-white' : 'bg-cyan-500 text-white'}`}>
                                                    {isCompleted ? '✓' : idx + 1}
                                                </div>
                                                <h3 className={`font-bold text-lg ${isLocked ? 'text-slate-500' : 'text-white group-hover:text-cyan-300'}`}>{s.title}</h3>
                                            </div>
                                            {!isLocked && (
                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded border border-cyan-500/30 text-cyan-400">
                                                    TRAINING
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-slate-400 mb-4 pl-11">{s.description}</p>
                                        
                                        {!isLocked && (
                                            <div className="pl-11">
                                                <button 
                                                    onClick={() => { playUiSfx('click'); onStart(true, s.id); }}
                                                    data-testid={`start-module-${s.id}`}
                                                    className="text-xs font-bold uppercase tracking-widest text-white border-b border-cyan-500 pb-0.5 hover:text-cyan-400 hover:border-cyan-400 transition-colors"
                                                >
                                                    Start Module →
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {tab === 'sandbox' && (
                    <div className="h-full min-h-0 flex flex-col">
                        {/* Header */}
                        <div className="mb-4 shrink-0">
                            <h2 className="text-xl font-bold text-white uppercase tracking-widest mb-2">Choose Your Industry</h2>
                            <p className="text-slate-400 text-sm">
                                Select an industry vertical to customize your compliance journey with relevant products, scenarios, and regulatory frameworks.
                            </p>
                        </div>

                        {/* Industry Category Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {INDUSTRY_CATEGORIES.map((category) => {
                                const isSelected = selectedCategory === category.id;
                                return (
                                    <button
                                        key={category.id}
                                        data-testid={`industry-${category.id}`}
                                        onClick={() => { playUiSfx('click'); setSelectedCategory(category.id); }}
                                        className={`p-5 rounded-xl border text-left transition-all ${
                                            isSelected 
                                                ? 'border-2 shadow-lg' 
                                                : 'border-white/10 hover:border-white/30 bg-white/5 hover:bg-white/10'
                                        }`}
                                        style={isSelected ? { 
                                            borderColor: category.colors.primary,
                                            backgroundColor: `${category.colors.primary}15`,
                                            boxShadow: `0 0 30px ${category.colors.primary}30`
                                        } : {}}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div 
                                                className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl shrink-0"
                                                style={{ backgroundColor: `${category.colors.primary}20` }}
                                            >
                                                {category.emoji}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-bold text-lg text-white">{category.name}</h3>
                                                    {isSelected && (
                                                        <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: category.colors.primary, color: '#000' }}>
                                                            SELECTED
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-500 mb-2">{category.tagline}</p>
                                                <p className="text-sm text-slate-400 mb-3 line-clamp-2">{category.description}</p>
                                                
                                                {/* Frameworks */}
                                                <div className="flex flex-wrap gap-1">
                                                    {category.frameworks.slice(0, 4).map((fw, idx) => (
                                                        <span 
                                                            key={idx}
                                                            className="px-2 py-0.5 rounded text-[10px] font-bold"
                                                            style={{ backgroundColor: `${category.colors.primary}20`, color: category.colors.primary }}
                                                        >
                                                            {fw}
                                                        </span>
                                                    ))}
                                                    {category.frameworks.length > 4 && (
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-white/10 text-slate-400">
                                                            +{category.frameworks.length - 4}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Difficulty Badge */}
                                        <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
                                            <span className="text-xs text-slate-500">Difficulty: <span className="text-white font-medium">{category.difficulty}</span></span>
                                            <span className="text-xs text-slate-500">{category.features.length} Features</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Action Buttons */}
                        <div className="pt-4 border-t border-white/10 shrink-0">
                            <div className="flex flex-col sm:flex-row gap-3">
                                {hasSave && (
                                    <button 
                                        onClick={() => { playUiSfx('click'); onResume(); }}
                                        data-testid="resume-game"
                                        className="flex-1 px-6 py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-500 transition-colors uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(79,70,229,0.3)]"
                                    >
                                        📂 Resume Saved Game
                                    </button>
                                )}

                                <button 
                                    onClick={() => { 
                                        playUiSfx('click'); 
                                        onStart(aiEnabled, undefined, selectedCategory || 'sandbox'); 
                                    }}
                                    data-testid="start-simulation"
                                    disabled={!selectedCategory}
                                    className={`flex-1 px-6 py-4 font-bold rounded-xl uppercase tracking-widest text-sm transition-all ${
                                        selectedCategory 
                                            ? 'bg-gradient-to-r from-emerald-600 to-cyan-600 text-white hover:from-emerald-500 hover:to-cyan-500 shadow-[0_0_20px_rgba(16,185,129,0.3)]' 
                                            : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                                    }`}
                                >
                                    {selectedCategory ? '🚀 Start New Simulation' : 'Select an Industry Above'}
                                </button>
                            </div>
                            
                            {selectedCategory && (
                                <p className="text-center text-xs text-slate-500 mt-3">
                                    Starting with <span className="text-emerald-400 font-medium">{INDUSTRY_CATEGORIES.find(c => c.id === selectedCategory)?.name}</span> industry configuration
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {tab === 'missions' && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-white uppercase tracking-widest">Available Missions</h2>
                            <span className="text-xs font-mono text-indigo-400">{SCENARIOS.length} FILES FOUND</span>
                        </div>
                        
                        <div className="grid gap-4">
                            {SCENARIOS.map((s) => (
                                <div key={s.id} className="group p-5 rounded-xl border border-white/10 hover:border-indigo-500/50 bg-white/5 hover:bg-white/10 transition-all cursor-default">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-lg text-white group-hover:text-indigo-300 transition-colors">{s.title}</h3>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                                            s.difficulty === 'Easy' ? 'border-emerald-500/30 text-emerald-400' :
                                            s.difficulty === 'Medium' ? 'border-yellow-500/30 text-yellow-400' :
                                            'border-rose-500/30 text-rose-400'
                                        }`}>{s.difficulty}</span>
                                    </div>
                                    <p className="text-xs text-indigo-400 font-mono mb-2 uppercase">{s.timeline}</p>
                                    <p className="text-sm text-slate-400 mb-4">{s.description}</p>
                                    <button
                                        type="button"
                                        data-testid={`start-mission-${s.id}`}
                                        onClick={() => { playUiSfx('click'); onStart(true, s.id); }}
                                        className="text-xs font-bold uppercase tracking-widest text-white border-b border-indigo-500 pb-0.5 hover:text-indigo-400 hover:border-indigo-400 transition-colors"
                                    >
                                        Launch Mission →
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === 'faq' && (
                    <div className="space-y-6 animate-fade-in">
                        <div className="flex justify-between items-center border-b border-white/10 pb-6">
                            <h2 className="text-xl font-bold text-emerald-400 uppercase tracking-widest">System Protocols & FAQ</h2>
                            <span className="text-xs font-mono text-slate-500">AUTHORIZED PERSONNEL ONLY</span>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> What is Compliance Command?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    Compliance Command is your architectural sandbox for financial chaos. It’s a simulation where you 
                                    explore the tension between profit and ethics without the real-world jail time. 
                                    Personalized by AI, the game throws unique crises at you based on your specific playstyle.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> Who created this system?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    We are a small team of engineers and designers obsessed with complex systems. 
                                    We wanted to build something that treats the player like an adult—complex, unforgiving, 
                                    and transparent. We believe the best games are the ones that respect your intelligence.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> How does the AI Risk Advisor work?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    The <span className="text-emerald-400">AI Risk Advisor</span> acts as your "Chief Risk Officer". 
                                    It analyzes your specific grid layout, budget, and threat levels 
                                    in real-time to generate tactical advice and dynamic story events.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> Is my data private?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    Absolutely. Your simulation state lives 100% locally in your browser's memory (`LocalStorage`). 
                                    We don't harvest your grid layouts or sell your strategies. Your data stays on your device.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> How much does it cost?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    Zero. Compliance Command is free. There are no ads, no trackers, and no "premium currency" to buy. 
                                    We built this to showcase what modern web technology can do, not to sell you gems.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> Is this appropriate for actual bankers?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    It might cause flashbacks to real compliance audits. Player discretion is advised.
                                    (The game is designed for ages 16+ due to complex themes of financial ruin).
                                </p>
                            </div>

                            <div className="border-t border-white/10 pt-6">
                                <h3 className="text-lg font-bold text-cyan-400 uppercase tracking-widest mb-4">Gameplay Mechanics</h3>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> What does a Jump Gate do?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    The Jump Gate is the most powerful building that unlocks <span className="text-emerald-400">emergency response capabilities</span>.
                                    It allows you to access the Security Operations Center (SOC) by pressing 'E' to contain breaches,
                                    unlock Series A funding rounds, and provides advanced threat reduction.
                                    Requires "Admin Access" research ($5,000) to unlock.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> Why can't I find buildings like Treasury or Load Balancers?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    Most advanced buildings are <span className="text-yellow-400">locked behind research</span>. Press 'R' to open the Dashboard,
                                    then go to the Research tab to unlock new technologies. Treasury requires "Deep Operations" research,
                                    while Load Balancers require "Zero Trust Architecture" research.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> How do I avoid zone violations and threat spikes?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    <span className="text-red-400">Never place Public buildings next to Restricted buildings</span> (causes violations).
                                    Create buffer zones with Data Pipes, Roads, or empty spaces. Build Firewalls and WAFs to reduce threat.
                                    Zone hierarchy: Core → Restricted → Security → DMZ → Public.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> What generates revenue in the game?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    <span className="text-emerald-400">Buildings:</span> The HUB (+$15K/tick), Treasury (+$31.5K/tick), Support (+$3.6K/tick)<br/>
                                    <span className="text-emerald-400">Products:</span> Digital Checking (+$45K), Crypto Trading (+$120K), High Yield Savings (+$30K), Credit Card (+$75K)<br/>
                                    Launch products via Dashboard → Products tab once you have the required buildings.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> What should I do during a data breach?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    <span className="text-red-400">Data breaches happen when threat reaches 100%</span> and cause major compliance damage.
                                    With a Jump Gate, press 'E' for SOC emergency actions. Without it, build Firewalls (-6 threat),
                                    SIEM (-8 threat), and delete Internet Gateways immediately.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <span className="text-emerald-500">Q:</span> I'm losing money fast and getting alerts - what should I do?
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed pl-6 border-l border-white/10">
                                    <span className="text-yellow-400">Emergency steps:</span> 1) Press 'R' → Research → Start "Compliance Ops" or "Basic Security"<br/>
                                    2) Take loans via Dashboard → Finance tab if you need emergency cash<br/>
                                    3) Launch Digital Checking product (free +$150/tick)<br/>
                                    4) Delete high-threat buildings like Internet Gateways
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

      </div>
    </div>
  );
};

export default StartScreen;
